import { UserEditor } from 'components';
import { DEFAULT_USER_DATA } from 'components/UserEditor/UserEditor';
import UserEditSubmitButton from 'components/UserEditSubmitButton/UserEditSubmitButton';
import { PermsContext } from 'contexts/PermsContext';
import React, { useContext, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getUserProfileInfo, updateUserProfileByAdmin } from 'services/user/user.service';
import { notifyErrorToast, notifySuccessToast } from 'utils/notifications/toast.utils';

export default function EditUser() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [formData, setFormData] = useState(DEFAULT_USER_DATA);
  const [errorMsg, setErrorMsg] = useState('');
  const [canAssignSM, setCanAssignSM] = useState(false);
  const [canAssignDM, setCanAssignDM] = useState(false);
  const perms = useContext(PermsContext);

  //Set form data on load
  useEffect(() => {
    if (!params.get('userid')) navigate('/system/createuser?tab=users');

    getUserProfileInfo(params.get('userid'))
      .then((res) => {
        if (res.status === 200) {
          const data = res.data.result;
          const { street, city, state, zipcode } = JSON.parse(
            data?.address || '{"street":"","city":"","state":"","zipcode":""}',
          );
          const { name, phone } = JSON.parse(data?.emergencyContact || '{"name":"","phone":""}');

          const userObject = {
            roles: JSON.parse(data?.roles || '[]'),
            base: data?.homebase || '',
            department: data?.department || '',
            rank: data?.rank || '',
            maintenance: data?.maintenanceOperatorLevel || 0,
            name: data?.name || '',
            email: data?.email || '',
            employeeId: data?.employeeId || '',
            phone: data?.phoneNumber || '',
            gender: data?.gender || 'Not Specified',
            birthday: data?.dob || null,
            isAdmin: data?.permission_level === 12 ?? false,
            serviceManager: Boolean(data?.service_manager) ?? false,
            deploymentManager: Boolean(data?.deployment_manager) ?? false,
            address: street || '',
            city: city || '',
            state: state || '',
            zipcode: zipcode || '',
            eContactName: name || '',
            eContactPhone: phone || '',
          };

          setFormData(userObject);
        }
      })
      .catch((err) => {
        console.error('Error from getUserProfileInfo:', err);
      });
  }, []);

  //Set permission to assign service manager role, only on mount
  useEffect(() => {
    const accessLevel = perms?.accessLevel;
    const serviceManagerFlag = perms?.serviceManagerFlag;
    setCanAssignSM(
      accessLevel === 13 ||
        accessLevel === 8 ||
        ((accessLevel === 11 || accessLevel === 12) && serviceManagerFlag),
    );
    // Deployment manager grant available to access levels 8, 11, 13.
    setCanAssignDM(accessLevel === 8 || accessLevel === 11 || accessLevel === 13);
  }, []);

  const onSubmit = async () => {
    //Make api calls
    try {
      const res = await updateUserProfileByAdmin(
        params.get('userid'),
        formData.roles,
        formData.base,
        formData.department,
        formData.rank,
        formData.name,
        formData.employeeId,
        formData.phone,
        formData.gender,
        formData.birthday,
        JSON.stringify({
          street: formData.address,
          city: formData.city,
          state: formData.state,
          zipcode: formData.zipcode,
        }),
        JSON.stringify({
          name: formData.eContactName,
          phone: formData.eContactPhone,
        }),
        formData.isAdmin || false,
        formData.maintenance !== 0,
        formData.maintenance,
        Number(formData.serviceManager),
        Number(formData.deploymentManager) || 0,
      );

      if (res?.status === 200) {
        notifySuccessToast('User', { isAddOperation: false });
        navigate(-1);
      }
    } catch (error) {
      console.error('Error in createFusionUser:', error);
      const message = error.message || error?.response?.data?.message || JSON.stringify(error);
      setErrorMsg(message);
      notifyErrorToast('User', { isAddOperation: false });
    }
  };

  const onDiscard = () => {
    setFormData(DEFAULT_USER_DATA);
    navigate(-1);
  };

  const submitButtonEnabled = () => {
    //check required values
    return (
      formData.name &&
      formData.email &&
      formData.roles &&
      formData.base &&
      formData.department &&
      formData.rank
    );
  };

  return (
    <div className="user-select-none w-100 h-100 d-flex flex-column">
      <UserEditor value={formData} onChange={setFormData} disabledFields={['email']} />
      <div className="d-flex flex-column flex-lg-row align-items-lg-baseline justify-content-lg-end mt-lg-2 gap-2">
        {errorMsg && <p data-error="true">{errorMsg}</p>}
        <div className="d-flex flex-column-reverse flex-lg-row-reverse align-items-lg-center gap-2 justify-content-center align-content-center">
          <UserEditSubmitButton
            onSubmit={onSubmit}
            disabled={!submitButtonEnabled()}
            title={'Edit User'}
          />
          {/* Cancel Button */}
          <Button variant="dark" className="p-3 h-100 border border-secondary" onClick={onDiscard}>
            Cancel
          </Button>
          {/* Permission checkboxes — stacked vertically */}
          <div className="d-flex flex-column gap-1 align-items-start">
            <Form.Check
              type="checkbox"
              checked={Boolean(formData.isAdmin)}
              onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked })}
              label="User is an Admin"
            />
            {canAssignSM && (
              <Form.Check
                type="checkbox"
                checked={Boolean(formData.serviceManager)}
                onChange={(e) =>
                  setFormData({ ...formData, serviceManager: e.target.checked ? 1 : 0 })
                }
                label="User is a Service Manager"
              />
            )}
            {canAssignDM && (
              <Form.Check
                type="checkbox"
                checked={Boolean(formData.deploymentManager)}
                onChange={(e) =>
                  setFormData({ ...formData, deploymentManager: e.target.checked ? 1 : 0 })
                }
                label="User is a Deployment Manager"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
